import type { T3InitialData, T3Link, T3Page } from '@t3headless/nuxt-typo3'
import { withoutTrailingSlash } from 'ufo'
import { type MaybeRef, computed, unref } from 'vue'

import { useCurrentLocale, useT3Options } from '#imports'

import type { ILocaleNavigation, INavigation, INavigationWithMedia } from '~ui/types'

export const usePageDataHeader = (
  _pageData: MaybeRef<T3Page | null>,
  _initialData: MaybeRef<T3InitialData | null>
) => {
  const pageData = computed(() => unref(_pageData))
  const initialData = computed(() => unref(_initialData))

  const { options } = useT3Options()
  const currentLocale = useCurrentLocale()

  const socialNavigation = computed(() => {
    const navigation = initialData.value?.socialMenu
    return Array.isArray(navigation) ? navigation : []
  })

  const themeColor = computed(() => {
    if (!pageData.value?.appearance?.headerTransparent) return undefined
    const themeColor = pageData.value?.content?.colPos0?.[0]?.appearance?.color
    return themeColor ? `theme-${themeColor}` : undefined
  })

  const homeLink = computed(() =>
    currentLocale.value === options.i18n.default ? '/' : `/${currentLocale.value}`
  )

  const navigation = computed(() => {
    const navigation = initialData.value?.navigation?.[0]?.children
    return Array.isArray(navigation) ? (navigation as Array<INavigation>) : []
  })

  const featuredNavigation = computed(() => {
    const navigation = initialData.value?.featuredNavigation
    return Array.isArray(navigation) ? (navigation as Array<INavigationWithMedia>) : []
  })

  const minorNavigation = computed(() => {
    const navigation = initialData.value?.minorNavigation
    return Array.isArray(navigation) ? (navigation as Array<INavigation>) : []
  })

  const localeNavigation = computed(() => {
    const localeArray = pageData.value?.i18n

    if (!Array.isArray(localeArray) || !localeArray.length) return []

    return localeArray.map((locale) => ({
      language: locale.navigationTitle,
      locale: locale.twoLetterIsoCode,
      localePath: withoutTrailingSlash(locale.link),
      active: locale.active,
      available: locale.available
    })) as Array<ILocaleNavigation>
  })

  const contextNavigation = computed(() => {
    const navigation = pageData.value?.contextMenu?.navigation
    if (!Array.isArray(navigation) || !navigation.length) return []
    return navigation.filter((entry) => !!entry.title.trim())
  })

  const contextNavigationAdditionalLink = computed(() => {
    const link = pageData.value?.contextMenu?.additionalLink
    return typeof link === 'object' ? link as T3Link : null
  })

  const contextNavigationIconButtons = computed(() => {
    return {
      iconButtonsText: pageData.value?.contextMenu?.iconButtonsText || '',
      iconButtons: pageData.value?.contextMenu?.iconButtons || []
    }
  })

  return {
    socialNavigation,
    themeColor,
    homeLink,
    navigation,
    featuredNavigation,
    minorNavigation,
    localeNavigation,
    contextNavigation,
    contextNavigationAdditionalLink,
    contextNavigationIconButtons
  }
}
